/**
 * Servicio de productos — Portal Cliente Menuee
 *
 * Responsabilidad: obtener el catálogo 3 capas desde Supabase
 * (categorias_producto, productos_maestro, productos_sucursal).
 *
 * Consume: supabaseClient. Delega transformación a mappers/productoMapper y categoriaMapper.
 *
 * Qué nunca debe hacer:
 * - Contener lógica visual ni JSX.
 * - Calcular precios finales de pedido ni totales con delivery.
 * - INSERT/UPDATE en catálogo (solo lectura en Portal).
 * - Consultar tablas legacy (productos).
 *
 * Referencia: P5_PORTAL_CLIENT_GUIDE.md — Cómo consultar productos.
 */

import { supabase } from "../supabaseClient";
import {
  mapProductosToView,
  mapProductosToExtras,
  filterProductosParaCarta,
  sortProductosForCarta,
} from "../mappers/productoMapper";
import {
  mapCategoriasToView,
  filterCategoriasParaCarta,
} from "../mappers/categoriaMapper";

/**
 * Select PostgREST — catálogo 3 capas (P5_PORTAL_CLIENT_GUIDE).
 */
export const QUERY_PRODUCTOS_SUCURSAL = `
  id,
  precio,
  activo,
  visible,
  producto:producto_maestro_id (
    id,
    nombre,
    descripcion,
    imagen_url,
    activo,
    armable,
    armable_cantidad,
    lista_armable_id,
    mostrar_extras,
    mostrar_categoria_extras,
    con_tamanos,
    lista_armable:lista_armable_id (
      id,
      nombre,
      ingredientes_con_precio,
      ingredientes:lista_armable_ingredientes!lista_armable_id (
        id,
        nombre,
        precio,
        activo,
        orden
      )
    ),
    tamanos:producto_tamanos!producto_maestro_id (
      id,
      nombre,
      precio,
      activo,
      orden
    ),
    ingredientes:producto_ingredientes!producto_maestro_id (
      id,
      nombre,
      precio,
      activo,
      orden
    ),
    categoria:categoria_producto_id (
      id,
      nombre,
      orden,
      codigo,
      activo
    )
  )
`;

const SELECT_CATEGORIAS = `
  id,
  nombre,
  orden,
  codigo,
  activo
`;

/**
 * @param {string} sucursalId
 * @returns {Promise<string|null>}
 */
async function getLocalIdFromSucursal(sucursalId) {
  const { data, error } = await supabase
    .from("sucursales")
    .select("local_id")
    .eq("id", sucursalId)
    .eq("activo", true)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data?.local_id ?? null;
}

/**
 * Filas crudas de productos_sucursal con joins (solo lectura).
 *
 * @param {string} sucursalId
 * @returns {Promise<Object[]>}
 */
export async function getProductosRaw(sucursalId) {
  if (!sucursalId) {
    return [];
  }

  const { data, error } = await supabase
    .from("productos_sucursal")
    .select(QUERY_PRODUCTOS_SUCURSAL)
    .eq("sucursal_id", sucursalId)
    .eq("activo", true)
    .eq("visible", true);

  if (error) {
    throw error;
  }

  return data ?? [];
}

/**
 * Filas crudas de categorias_producto del local, ordenadas por orden y nombre.
 *
 * @param {string} localId
 * @returns {Promise<Object[]>}
 */
export async function getCategoriasRaw(localId) {
  if (!localId) {
    return [];
  }

  const { data, error } = await supabase
    .from("categorias_producto")
    .select(SELECT_CATEGORIAS)
    .eq("local_id", localId)
    .eq("activo", true)
    .order("orden", { ascending: true })
    .order("nombre", { ascending: true });

  if (error) {
    throw error;
  }

  return data ?? [];
}

const productosRawPorSucursal = new Map();
const categoriasRawPorLocal = new Map();

/**
 * Reutiliza la consulta de productos de la sucursal durante la sesión
 * (listado de carta y extras del modal).
 *
 * @param {string} sucursalId
 * @returns {Promise<Object[]>}
 */
function getProductosRawCached(sucursalId) {
  if (!sucursalId) {
    return Promise.resolve([]);
  }

  const existente = productosRawPorSucursal.get(sucursalId);
  if (existente) {
    return existente;
  }

  const pending = getProductosRaw(sucursalId).catch((error) => {
    productosRawPorSucursal.delete(sucursalId);
    throw error;
  });
  productosRawPorSucursal.set(sucursalId, pending);
  return pending;
}

/**
 * Categorías del local: no cambian al cambiar de sucursal del mismo tenant.
 *
 * @param {string} localId
 * @returns {Promise<Object[]>}
 */
function getCategoriasRawCached(localId) {
  if (!localId) {
    return Promise.resolve([]);
  }

  const existente = categoriasRawPorLocal.get(localId);
  if (existente) {
    return existente;
  }

  const pending = getCategoriasRaw(localId).catch((error) => {
    categoriasRawPorLocal.delete(localId);
    throw error;
  });
  categoriasRawPorLocal.set(localId, pending);
  return pending;
}

/**
 * Catálogo completo para la carta: productos normalizados + categorías oficiales.
 *
 * @param {string} sucursalId
 * @param {string|null} [localId=null] — si ya se resolvió el local, evita un SELECT extra a sucursales.
 * @returns {Promise<{ productos: import('../mappers/productoMapper').ProductoLegacy[], categorias: import('../mappers/categoriaMapper').CategoriaView[], extras: import('../mappers/productoMapper').ProductoLegacy[] }>}
 */
export async function getCatalogo(sucursalId, localId = null) {
  if (!sucursalId) {
    return { productos: [], categorias: [], extras: [] };
  }

  const resolvedLocalId = localId || (await getLocalIdFromSucursal(sucursalId));

  const [productoRows, categoriaRows] = await Promise.all([
    getProductosRawCached(sucursalId),
    getCategoriasRawCached(resolvedLocalId),
  ]);

  const productosTodos = mapProductosToView(productoRows);
  const productos = sortProductosForCarta(filterProductosParaCarta(productosTodos));
  const categorias = filterCategoriasParaCarta(
    mapCategoriasToView(categoriaRows),
    productosTodos
  );
  const extras = mapProductosToExtras(productoRows);

  return { productos, categorias, extras };
}

/**
 * Extras disponibles (productos_sucursal en categoría extra) para ModalProducto.
 *
 * @param {string} sucursalId
 * @returns {Promise<import('../mappers/productoMapper').ProductoLegacy[]>}
 */
export async function getExtras(sucursalId) {
  if (!sucursalId) {
    return [];
  }

  const rows = await getProductosRawCached(sucursalId);
  return mapProductosToExtras(rows);
}

/**
 * Productos normalizados para componentes (ProductoCard, ModalProducto).
 *
 * @param {string} sucursalId
 * @returns {Promise<import('../mappers/productoMapper').ProductoLegacy[]>}
 */
export async function getProductos(sucursalId) {
  const { productos } = await getCatalogo(sucursalId);
  return productos;
}
